/** What the setup gate needs to know about the session and the journey read. */
export interface SetupGateInput {
  role: string | undefined;
  /** Set when the session is acting for a workspace under the account, not the account itself. */
  endOrgId: string | undefined;
  /** `company` | `personal` | `partner` | `end_org` | `platform`. Optional:
   *  absent simply means "not personal", which is the permissive case. */
  orgType?: string | undefined;
  /** Whether the setup-journey query has settled. */
  isFetched: boolean;
  status: string | undefined;
  /** The org the SERVER resolved the journey for, from the response body.
   *  Null when it could not resolve one — see the note in shouldSendToSetup. */
  journeyOrgId?: string | null;
  /** The org this session is acting as, from the token/context. */
  actingOrgId?: string | undefined;
}

/** Roles that can actually complete the wizard: pick connectors, create a widget. */
const BUILDER_ROLES = new Set(["owner", "admin", "developer"]);

/**
 * Should this session be sent to the guided first-run setup?
 *
 * `/onboarding` is a standalone route with no DashboardLayout, so it has no
 * sidebar — and the account switcher lives in the sidebar. That makes a wrong
 * "yes" here unrecoverable rather than merely annoying: the redirect wraps every
 * dashboard route, so there is no page left to navigate to and no control left
 * to switch org with.
 *
 * That is what trapped a partner admin who switched into a customer workspace.
 * The journey query is keyed on the acting org, the customer's workspace has
 * never been onboarded, so it answered "in_progress" and the whole session was
 * pinned to a wizard for someone else's workspace with no way back.
 *
 * A session acting for a workspace it switched into is therefore never a
 * first-run builder, whatever the journey says about that workspace.
 */
export function shouldSendToSetup(input: SetupGateInput): boolean {
  // Acting for a workspace under the account (switched account, or
  // impersonation) — not this user's own first run.
  if (input.endOrgId) return false;
  // A personal org has no customers to embed for, so it cannot finish a wizard
  // whose three steps are Connect, Embed and Verify. The journey defaults to
  // "in_progress" for any org with nothing stored, so a personal signup was
  // pinned to a flow it could never complete — the same unrecoverable trap as
  // above, reached by a different route. Alerts was simply the page someone
  // happened to notice was missing.
  if (input.orgType === "personal") return false;
  if (!input.role || !BUILDER_ROLES.has(input.role)) return false;
  // A loading or failed read renders the dashboard: a slow or broken onboarding
  // API must never hold the app behind a spinner.
  if (!input.isFetched) return false;

  /* The answer has to be ABOUT the org we are looking at, and the server has to
     have found one.

     GET /api/v1/onboarding/setup resolves its org from
     `memberships WHERE is_default = true AND status = 'active'`, and when that
     finds nothing it answers HTTP 200 with `{ orgId: null, status:
     "in_progress" }` — the default journey. A 200 is not an error, so the
     client's "a failed read means completed" guard never fired, and a
     fully-established org was dropped into the first-run wizard. That is the
     intermittent redirect that got reported: the default-membership row is not
     guaranteed to be there, and is briefly absent while provisioning backfills
     memberships.

     It also resolves the user's DEFAULT org rather than the acting one, so
     after an account switch the answer describes a different org than the one
     on screen. Requiring the two to agree covers both. */
  if (!input.journeyOrgId) return false;
  if (input.actingOrgId && input.journeyOrgId !== input.actingOrgId) return false;

  return input.status === "in_progress";
}

/** Whether the journey query should run at all. Mirrors the gate: asking about
 *  a journey that can never send anyone anywhere is a wasted round trip on
 *  every page load. */
export function setupJourneyEnabled(
  role: string | undefined,
  endOrgId: string | undefined,
  orgType?: string | undefined,
): boolean {
  return !endOrgId && orgType !== "personal" && !!role && BUILDER_ROLES.has(role);
}
